import { Button, Card, Empty, Flex, Input, Space, Table, Typography } from 'antd';
import type { TableProps } from 'antd';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

const { Title } = Typography;

export interface ListPageProps<T> {
  title: string;
  /** Placeholder for the search box. Omit to hide search entirely. */
  searchPlaceholder?: string;
  searchValue?: string;
  onSearch?: (value: string) => void;
  /** Right-aligned actions in the header — "Add customer", "Export CSV". */
  actions?: ReactNode;
  columns: TableProps<T>['columns'];
  rows: T[];
  rowKey: (row: T) => string;
  loading?: boolean;
  emptyDescription?: string;
  onRowClick?: (row: T) => void;
  /** Rows that open nothing are not styled as links. Defaults to every row when onRowClick is set. */
  isRowClickable?: (row: T) => boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  /** Card-per-row renderer used below 768px, where a dense table is unusable. */
  renderMobileRow: (row: T) => ReactNode;
  /**
   * A list inside a tab rather than a page of its own (the customer page's
   * module tabs): a section heading and a small table.
   */
  compact?: boolean;
}

/**
 * One of the three screen primitives from CLAUDE.md §10. Every list in every
 * later phase composes this rather than growing a fourth variant of the same
 * table — build the customer screen first and extract afterwards, and by Phase 4
 * there are three subtly different tables with three different empty states.
 *
 * The mobile treatment is a card list, not a horizontally scrolling table. A
 * service advisor checking a follow-up on a phone cannot scroll a twelve-column
 * table one-handed, which is what §14's 390px requirement is actually about.
 */
export function ListPage<T>({
  title,
  searchPlaceholder,
  searchValue,
  onSearch,
  actions,
  columns,
  rows,
  rowKey,
  loading = false,
  emptyDescription,
  onRowClick,
  isRowClickable,
  hasMore = false,
  onLoadMore,
  renderMobileRow,
  compact = false,
}: ListPageProps<T>): ReactNode {
  const { t } = useTranslation();

  const clickable = (row: T): boolean => onRowClick !== undefined && (isRowClickable?.(row) ?? true);
  const empty = <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyDescription} />;

  return (
    <Flex vertical gap={compact ? 8 : 16}>
      <Flex align="center" justify="space-between" wrap gap={12}>
        <Title level={compact ? 5 : 4} style={{ margin: 0 }}>
          {title}
        </Title>
        <Space wrap>{actions}</Space>
      </Flex>

      {onSearch !== undefined && (
        <Input.Search
          allowClear
          placeholder={searchPlaceholder}
          defaultValue={searchValue}
          onSearch={onSearch}
          size="large"
          style={{ maxWidth: 480 }}
        />
      )}

      {/* Table on wide screens, cards below 768px. Both render the same rows. */}
      <div className="nks-list-table">
        <Table<T>
          columns={columns}
          dataSource={rows}
          rowKey={rowKey}
          loading={loading}
          pagination={false}
          size={compact ? 'small' : 'middle'}
          locale={{ emptyText: empty }}
          onRow={(row) => ({
            onClick: () => {
              if (clickable(row)) onRowClick?.(row);
            },
            style: clickable(row) ? { cursor: 'pointer' } : undefined,
          })}
        />
      </div>

      <div className="nks-list-cards">
        {rows.length === 0 && !loading ? (
          <Card>{empty}</Card>
        ) : (
          <Flex vertical gap={8}>
            {rows.map((row) => (
              <Card
                key={rowKey(row)}
                size="small"
                hoverable={clickable(row)}
                onClick={() => {
                  if (clickable(row)) onRowClick?.(row);
                }}
                styles={{ body: { padding: 12 } }}
              >
                {renderMobileRow(row)}
              </Card>
            ))}
          </Flex>
        )}
      </div>

      {hasMore && onLoadMore !== undefined && (
        <Button onClick={onLoadMore} loading={loading} size="large" block>
          {t('customers.loadMore')}
        </Button>
      )}
    </Flex>
  );
}
